/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import moment from 'moment';

// Component imports
import { Button, Col, Collapse, Divider, Empty, Form, InputNumber, Radio, Row, Select, Space, Spin, Switch, TimePicker, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Prompt from '../UI/Prompt';
import BotAvatar from '../UI/BotAvatar';
import FormCard from './FormCard';
import { WebchatIcon } from '../Icons/platformIcons/Webchat';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, LANGUAGE_OPTIONS } from '../../constants/main';
import { IS_LIVECHAT_ASSISTANT_ENABLED, IS_LIVECHAT_REMINDER_ENABLED, IS_LIVECHAT_TRANSLATION_ENABLED } from '../../constants/env';
import { LIVECHAT_MODES } from '../../constants/liveChat';
import { useParams } from 'react-router-dom';
import { useFetchLiveChatApplicationSettingsQuery, useUpdateLiveChatSettingsMutation } from '../../store/liveChatSettings/liveChatSettingsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchBotDeploymentsQuery } from '../../store/botDeployments/botDeploymentsApi';
import { useTranslation } from 'react-i18next';
import { useCreateGroupBusinessHourMutation, useDeleteGroupBusinessHourMutation, useFetchUserGroupsQuery, useLazyFetchGroupBusinessHourQuery, useUpdateGroupBusinessHourMutation } from '../../store/userGroups/userGroupsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import is from '../../utils/is';
import timeToMinutesAfterMidnight from '../../utils/date/timeToMinutesAfterMidnight';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const HOURS = [...Array(24).keys()];
const MINUTES = Array.from(Array(12), (_, x) => x * 5);

const LiveChatGeneralSettingsForm = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const [form] = Form.useForm();

  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [, updateState] = useState(); // Hook to force a component re-render
  const [canSubmit, setCanSubmit] = useState(false);
  const [isBotDeleted, setIsBotDeleted] = useState(true); //init as true to delay deployments query if bot is deleted
  const [isDeploymentDeleted, setIsDeploymentDeleted] = useState(false);

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const { data: livechatSettings } = useFetchLiveChatApplicationSettingsQuery({ orgId }, { skip: !orgId });
  const { data: botDeployments, isFetching: isFetchingBotDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId: selectedTenantId }, { skip: !IS_LIVECHAT_ASSISTANT_ENABLED || !selectedTenantId || isBotDeleted });
  const [updateLivechatApplicationSettings, { isLoading: isUpdatingLivechatApplicationSettings }] = useUpdateLiveChatSettingsMutation();
  const { data: userGroups, isFetching: isFetchingUserGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 500 } });
  const [fetchGroupBusinessHours, { currentData: userGroupBusinessHours, isLoading: isLoadingBusinessHours }] = useLazyFetchGroupBusinessHourQuery();
  const [createGroupBusinessHour, { isLoading: isCreatingUserGroup }] = useCreateGroupBusinessHourMutation();
  const [updateGroupBusinessHour, { isLoading: isUpdatingUserGroup }] = useUpdateGroupBusinessHourMutation();
  const [deleteGroupBusinessHour, { isLoading: isDeletingUserGroup }] = useDeleteGroupBusinessHourMutation();

  useEffect(() => {
    if (!livechatSettings) {
      return;
    }

    const { livechatMode, idleReminderThreshold, botAssistantSettings, isTranslationEnabled, agentDefaultLanguage } = livechatSettings;
    form.setFieldsValue({
      livechatMode,
      enableReminder: is.truthy(idleReminderThreshold),
      idleReminderThreshold: idleReminderThreshold ? Math.ceil(idleReminderThreshold / 60) : null,
      isTranslationEnabled,
      agentDefaultLanguage
    });

    if (IS_LIVECHAT_ASSISTANT_ENABLED) {
      form.setFieldsValue({
        enabled: botAssistantSettings.enabled,
        tenantId: botAssistantSettings.tenantId,
        botDeploymentId: botAssistantSettings.botDeploymentId
      });
      setSelectedTenantId(botAssistantSettings.tenantId);
    }
  }, [livechatSettings]);

  useEffect(() => {
    if (IS_LIVECHAT_ASSISTANT_ENABLED) {
      let botExists = true;
      let deploymentExists = true;

      if (form.getFieldValue('tenantId') && bots?.items) {
        botExists = bots.items.find(bot => bot.id === form.getFieldValue('tenantId')) || false;
        setIsBotDeleted(!botExists);
      }

      if (form.getFieldValue('botDeploymentId') && botDeployments?.items) {
        deploymentExists = botDeployments.items.find(deployment => deployment.id === form.getFieldValue('botDeploymentId')) || false;
        setIsDeploymentDeleted(!deploymentExists);
      }

      if (!botExists || !deploymentExists) {
        form.validateFields();
      }
    }
  }, [livechatSettings, bots, botDeployments]);// livechatSettings dependency for 1st error check because bots is cached and botDeployments are not fetched if isBotDeleted

  const onSave = async () => {
    const mainFields = ['livechatMode', 'enableReminder', 'idleReminderThreshold', 'isTranslationEnabled', 'agentDefaultLanguage'];
    const assistantFields = ['enabled', 'tenantId', 'botDeploymentId'];
    const groupFields = ['group', 'businessHours'];

    try {
      await form.validateFields(mainFields);
    } catch (e) {
      return;
    }

    const groupValues = form.getFieldsValue(groupFields);

    let assistantValues = null;

    if (IS_LIVECHAT_ASSISTANT_ENABLED) {
      try {
        await form.validateFields(assistantFields);
        assistantValues = form.getFieldsValue(assistantFields);
      } catch (e) {
        return;
      }
    }
    const orgValues = form.getFieldsValue(mainFields);

    const formmatedOrgValues = {
      ...orgValues,
      idleReminderThreshold: orgValues.enableReminder ? orgValues.idleReminderThreshold * 60 : null,
      ...(assistantValues ? { botAssistantSettings: assistantValues } : {})
    };

    delete formmatedOrgValues.enableReminder;
    const response = await updateLivechatApplicationSettings({ orgId, data: formmatedOrgValues });
    let groupResponse = true;
    let deleteResponse = true;

    if (groupValues.group) {
      for (const entry of groupValues.businessHours) {
        const entryCopy = {
          day: entry.day,
          opensAt: entry.opensAt && timeToMinutesAfterMidnight(entry.opensAt),
          closesAt: entry.closesAt && timeToMinutesAfterMidnight(entry.closesAt)
        };
        if (entry.id) {
          groupResponse = await updateGroupBusinessHour({ orgId, userGroupId: groupValues.group, businessHourId: entry.id, data: entryCopy });
        } else {
          groupResponse = await createGroupBusinessHour({ orgId, userGroupId: groupValues.group, data: entryCopy });
        }
      }
      for (const entryToDelete of userGroupBusinessHours.filter(item => !groupValues.businessHours.find(hour => item.id === hour.id))) {
        deleteResponse = await deleteGroupBusinessHour({ orgId, userGroupId: groupValues.group, businessHourId: entryToDelete.id });
      }
    }

    if (response && groupResponse && deleteResponse) {
      setCanSubmit(false);
    }
  };

  const SaveButton = () => (
    <Button
      type="primary"
      size="large"
      onClick={onSave}
      disabled={!canSubmit}
      loading={isUpdatingLivechatApplicationSettings || isCreatingUserGroup || isUpdatingUserGroup || isDeletingUserGroup}
    >
      {t('Save Changes')}
    </Button>
  );

  const showDeploymentWarning = IS_LIVECHAT_ASSISTANT_ENABLED ? (isDeploymentDeleted || (isBotDeleted && form.getFieldValue('botDeploymentId'))) : false;

  return (
    <FormCard
      form={form}
      layout="vertical"
      onValuesChange={(_, allValues) => {
        updateState(allValues);
        setCanSubmit(true);
      }}
      footerActions={[
        <Fragment key="footer-actions">
          <SaveButton />
          <Prompt when={canSubmit} message={t('Changes that you made may not be saved.')} />
        </Fragment>
      ]}
    >
      <Row
        wrap
        gutter={formProps.rowGutter}
        className="mb-4"
      >
        <Col xs={formProps.mainColSpan}>
          <Typography.Paragraph className="mb-0">{t('General Settings')}</Typography.Paragraph>
          <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Configure the Workspace\'s LiveChat settings')}</Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
          <Typography.Paragraph className="mb-2">{t('LiveChat Mode')}</Typography.Paragraph>

          <Form.Item
            name="livechatMode"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio
                  value={LIVECHAT_MODES.SEMI_PRIVATE}
                >
                  {t('Private')}
                  <Tooltip
                    title={t('Show conversations only to LiveChat participants')}
                  >
                    <QuestionCircleOutlined className="ml-1" />
                  </Tooltip>
                </Radio>
                <Radio
                  value={LIVECHAT_MODES.PUBLIC}
                >
                  {t('Public')}
                  <Tooltip
                    title={t('Show conversations to all LiveChat agents')}
                  >
                    <QuestionCircleOutlined className="ml-1" />
                  </Tooltip>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {
            IS_LIVECHAT_REMINDER_ENABLED ? (
              <Fragment>
                <Typography.Paragraph className="mb-2">
                  {t('Notification for inactivity in conversation')}

                  <Tooltip title={t('Send a notification when a conversation has had no activity for a certain amount of time (in minutes)')}>
                    <QuestionCircleOutlined className="ml-1"/>
                  </Tooltip>
                </Typography.Paragraph>

                <Form.Item
                  name="enableReminder"
                  className="mb-2"
                  valuePropName="checked"
                >
                  <Switch
                    onChange={(value) => {
                      if (!value) {
                        form.setFieldsValue({ idleReminderThreshold: null });
                      } else {
                        form.setFieldsValue({ idleReminderThreshold: 1 });
                      }
                    }}
                    disabled={!livechatSettings}
                  />
                </Form.Item>

                <Form.Item
                  shouldUpdate
                  noStyle
                >
                  {() => (
                    <Form.Item
                      name="idleReminderThreshold"
                      rules={[{ required: form.getFieldValue('enableReminder'), message: t('Please enter a value.') }]}
                    >
                      <InputNumber
                        disabled={!form.getFieldValue('enableReminder')}
                        className="w-100"
                        placeholder={t('Enter a value in minutes')}
                        min={1}
                        size="large"
                        style={{ maxWidth: formProps.maxInputWidth }}
                        onBlur={({ target }) => {
                          if (livechatSettings.idleReminderThreshold !== Number(target.value) * 60) {
                            form.submit();
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Fragment>
            ) : null
          }
        </Col>
      </Row>

      {
        IS_LIVECHAT_TRANSLATION_ENABLED ? (
          <Fragment>
            <Divider />

            <Row
              wrap
              gutter={formProps.rowGutter}
              className="mb-4"
            >
              <Col xs={formProps.mainColSpan}>
                <Typography.Paragraph className="mb-0">{t('Workspace Language')}</Typography.Paragraph>
                <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Set your agents language. This setting applies to the Real Time Translation tool.')}</Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden" >
                <Typography.Paragraph className="mb-2">
                  {t('Enable Translation')}
                </Typography.Paragraph>

                <Form.Item
                  name="isTranslationEnabled"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch
                    checked
                    disabled={!livechatSettings}
                  />
                </Form.Item>

                <Form.Item
                  name="agentDefaultLanguage"
                  className="mt-3"
                >
                  <Select
                    placeholder={t('Choose a deployment language')}
                    size="large"
                    allowClear
                    showSearch
                    style={{ maxWidth: formProps.maxInputWidth }}
                    filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                    options={LANGUAGE_OPTIONS
                      .map(language => ({
                        label: <Space size={4}>{LANGUAGE_OPTIONS.find(lang => lang.key === language.key)?.icon} {language.label}</Space>,
                        value: language.key,
                        searchValue: language.label
                      }))
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        ) : null
      }

      {
        IS_LIVECHAT_ASSISTANT_ENABLED ? (
          <Fragment>
            <Divider />

            <Row
              wrap
              gutter={formProps.rowGutter}
              className="mb-4"
            >
              <Col xs={formProps.mainColSpan}>
                <Typography.Paragraph className="mb-0">{t('AI Agent Assistant')}</Typography.Paragraph>
                <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Enable AI features for LiveChat')}</Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
                <Typography.Paragraph className="mb-2">{t('Enable Agent\'s AI Assistant')}</Typography.Paragraph>

                <Form.Item
                  name="enabled"
                  valuePropName="checked"
                >
                  <Switch
                    onChange={(value) => {
                      if (!value) {
                        return form.setFieldsValue({ tenantId: null, botDeploymentId: null });
                      }
                    }}
                    disabled={!livechatSettings}
                  />
                </Form.Item>

                <Typography.Paragraph className="mb-2">{t('Select Agent')}</Typography.Paragraph>

                <Form.Item
                  name="tenantId"
                  className="pb-2"
                  rules={[
                    { required: form.getFieldValue('enabled'), message: t('Please select an agent.', { context: 'AI agent' }) },
                    {
                      validator: (_, value) => {
                        if (isBotDeleted && value) {
                          return Promise.reject(
                            new Error(
                              t('The selected Agent with ID {{botID}} has been deleted. Please select another one.', { botID: selectedTenantId })
                            )
                          );
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Select
                    disabled={!form.getFieldValue('enabled')}
                    allowClear
                    className="fs-base"
                    onChange={(value) => {
                      setSelectedTenantId(value);
                      setIsBotDeleted(false);
                      setIsDeploymentDeleted(false);
                      form.setFieldsValue({ botDeploymentId: null });
                    }}
                    notFoundContent={
                      <Row className="p-4" justify="center" align="middle">
                        {
                          isFetchingBots ? (
                            <Spin />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No agents found', { context: 'AI agents' })} />
                          )
                        }
                      </Row>
                    }
                    placeholder={t('Select an agent', { context: 'AI agent' })}
                    onSelect={setSelectedTenantId}
                    showSearch
                    size="large"
                    filterOption={(input, option) =>
                      bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ maxWidth: formProps.maxInputWidth }}
                  >
                    {
                      bots && is.array(bots.items) && bots.items.map(bot => (
                        <Select.Option key={bot.id} value={bot.id}>
                          <BotAvatar bot={bot} avatarSize={28} />
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>

                <Typography.Paragraph className="mb-2">{t('Select WebChat Deployment')}</Typography.Paragraph>

                <Form.Item
                  name="botDeploymentId"
                  rules={[
                    {
                      required: form.getFieldValue('enabled'),
                      message: t('Please select a deployment.')
                    },
                    {
                      validator: (_, value) => {
                        if (showDeploymentWarning && value) {
                          return Promise.reject(
                            new Error(
                              t('The selected Deployment with ID {{deploymentID}} has been deleted. Please select another one.', {
                                deploymentID: form.getFieldValue('botDeploymentId')
                              })
                            )
                          );
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Select
                    disabled={!form.getFieldValue('tenantId')}
                    allowClear
                    className="fs-base"
                    onChange={() => setIsDeploymentDeleted(false)}
                    notFoundContent={
                      <Row className="p-4" justify="center" align="middle">
                        {
                          isFetchingBotDeployments ? (
                            <Spin />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No deployments found')} />
                          )
                        }
                      </Row>
                    }
                    placeholder={t('Select a deployment')}
                    showSearch
                    size="large"
                    filterOption={(input, option) =>
                      botDeployments && is.array(botDeployments.items) && botDeployments.items.find(deployment => deployment.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ maxWidth: formProps.maxInputWidth }}
                  >
                    {
                      botDeployments && is.array(botDeployments.items) && botDeployments.items.filter(deployment => (deployment.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key && !deployment.isPreview)).map(deployment => (
                        <Select.Option key={deployment.id} value={deployment.id}>
                          <span className="bot-avatar">
                            <WebchatIcon className="fs-xl mr-1" />
                            <Typography.Text ellipsis className="ml-3">
                              {deployment.displayName}
                            </Typography.Text>
                          </span>
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        ) : null
      }

      <Collapse
        ghost
        defaultActiveKey={['groupSettings']}
      >
        <Collapse.Panel
          className="ant-collapse-p-0"
          showArrow={false}
          key="groupSettings"
          header={
            <Divider className="fs-base ant-divider-primary py-3" orientation="left">
              <Typography.Text strong>
                <span className="title">
                  {t('User Group LiveChat Settings')}
                </span>

                <span className="ml-3 icon">
                  <ArrowDownIcon className="fs-base"/>
                </span>
              </Typography.Text>
            </Divider>
          }
        >
          <Row
            wrap
            gutter={formProps.rowGutter}
            className="mb-4"
          >
            <Col xs={formProps.mainColSpan}>
              <Typography.Paragraph className="mb-0">{t('Select User Group')}</Typography.Paragraph>
              <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Select a user group to configure')}</Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
              <Form.Item
                label={t('Settings for this User Group')}
                name="group"
              >
                <Select
                  allowClear
                  className="fs-base"
                  onClear={() => {
                    form.resetFields();
                  }}
                  notFoundContent={
                    <Row className="p-4" justify="center" align="middle">
                      {
                        isFetchingUserGroups ? (
                          <Spin />
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No user groups found')} />
                        )
                      }
                    </Row>
                  }
                  placeholder={t('Select a user group')}
                  onSelect={async (id) => {
                    const businessHours = await fetchGroupBusinessHours({ orgId, userGroupId: id, urlParams: {}, isLCM: true }).unwrap();
                    if (!businessHours || !businessHours.length) {
                      return form.setFieldsValue({
                        businessHours: []
                      });
                    }

                    form.setFieldsValue({
                      businessHours: (businessHours ?? []).map(slot => (
                        {
                          id: slot.id,
                          day: slot.day,
                          opensAt: is.number(slot.opensAt) && moment().startOf('day').add(slot.opensAt, 'minutes'),
                          closesAt: is.number(slot.closesAt) && moment().startOf('day').add(slot.closesAt, 'minutes')
                        }
                      ))
                    });
                  }}
                  showSearch
                  size="large"
                  filterOption={(input, option) =>
                    userGroups && is.array(userGroups.items) && userGroups.items.find(group => group.id === option.value).name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ maxWidth: formProps.maxInputWidth }}
                >
                  {
                    userGroups
                      && is.array(userGroups.items)
                      && userGroups.items.map(group => (
                        <Select.Option key={group.id} value={group.id}>
                          {group.name}
                        </Select.Option>
                      ))
                  }
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row
            wrap
            gutter={formProps.rowGutter}
            className="mb-4"
          >
            <Col xs={formProps.mainColSpan}>
              <Typography.Paragraph className="mb-0">{t('Business Hours')}</Typography.Paragraph>
              <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Configure the LiveChat operating hours per user group')}</Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                shouldUpdate
                className="mb-2"
              >
                {() => (
                  <Form.List
                    name="businessHours"
                  >
                    {(fields, { add, remove }) => (
                      <div style={{ maxWidth: formProps.maxInputWidth }}>
                        <Typography.Paragraph
                          className="fs-sm"
                        >
                          {t('Timezone: ') + organization?.timezone ?? t('Unknown')}
                        </Typography.Paragraph>

                        <div
                          className="bg-body p-4 mb-2 overflow-y-auto"
                          style={{ borderRadius: 4, maxHeight: 360 }}
                        >
                          {
                            isLoadingBusinessHours ? (
                              <Row justify="center" align="middle"><Spin /></Row>
                            )
                              : !fields.length ? (
                                <Typography.Paragraph
                                  className="fs-sm text-muted fw-bold text-center"
                                  style={{ margin: 'auto' }}
                                >
                                  {t('No time slots configured')}
                                </Typography.Paragraph>
                              ) : null
                          }

                          {
                            fields.map(({ key, name, ...restField }, index) => (
                              <Form.Item
                                key={key}
                                className="m-0"
                              >
                                <Row gutter={8} wrap={false} className="position-relative">
                                  <Col flex="1 1 35%">
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'day']}
                                      label={t('Day of Week')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                    >
                                      <Select
                                        placeholder={t('Field name')}
                                        size="large"
                                        className="fs-base"
                                        placement="topLeft"
                                        allowClear={false}
                                        options={[
                                          {
                                            label: t('Monday'),
                                            value: 1
                                          },
                                          {
                                            label: t('Tuesday'),
                                            value: 2
                                          },
                                          {
                                            label: t('Wednesday'),
                                            value: 3
                                          },
                                          {
                                            label: t('Thursday'),
                                            value: 4
                                          },
                                          {
                                            label: t('Friday'),
                                            value: 5
                                          },
                                          {
                                            label: t('Saturday'),
                                            value: 6
                                          },
                                          {
                                            label: t('Sunday'),
                                            value: 0
                                          }
                                        ]}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col flex="1 1 25%">
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'opensAt']}
                                      label={t('Start Time')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                    >
                                      <TimePicker
                                        size="large"
                                        onChange={(value) => {
                                          const opensHour = moment(value).hour();
                                          const closesHour = moment(form.getFieldValue(['businessHours', name, 'closesAt'])).hour();
                                          if (opensHour !== closesHour) {
                                            return;
                                          }
                                          const opensMinutes = moment(value).minutes();
                                          const closesMinutes = moment(form.getFieldValue(['businessHours', name, 'closesAt'])).minutes();

                                          if (opensMinutes > closesMinutes) {
                                            form.setFieldValue(['businessHours', name, 'opensAt'], moment(value).minutes(0));
                                          }
                                        }}
                                        disabledTime={() => ({
                                          disabledHours: () => {
                                            const hoursToDisable = [];
                                            for (const hour of HOURS) {
                                              const momentHour = moment(form.getFieldValue(['businessHours', name, 'closesAt'])).hour();
                                              const momentMinutes = moment(form.getFieldValue(['businessHours', name, 'closesAt'])).minutes();
                                              const shouldDisable = hour > momentHour || (hour === momentHour && momentMinutes < 5);
                                              if (shouldDisable) {
                                                hoursToDisable.push(hour);
                                              }
                                            }
                                            return hoursToDisable;
                                          },
                                          disabledMinutes: (hour) => {
                                            const minutesToDisable = [];
                                            for (const minute of MINUTES) {
                                              const momentMinute = moment().hour(hour).minutes(0).add(minute, 'minutes');
                                              const shouldDisable = moment(form.getFieldValue(['businessHours', name, 'closesAt'])).isSameOrBefore(momentMinute);
                                              if (shouldDisable) {
                                                minutesToDisable.push(minute);
                                              }
                                            }
                                            return minutesToDisable;
                                          }
                                        })}
                                        placement="topLeft"
                                        format="HH:mm"
                                        minuteStep={5}
                                        showNow={false}
                                        allowClear={false}
                                        className="fs-base"
                                        style={{ minHeight: 40 }}
                                      />

                                    </Form.Item>
                                  </Col>

                                  <Col flex="1 1 25%">
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'closesAt']}
                                      label={t('End Time')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                    >
                                      <TimePicker
                                        size="large"
                                        onChange={(value) => {
                                          const closesHour = moment(value).hour();
                                          const opensHour = moment(form.getFieldValue(['businessHours', name, 'opensAt'])).hour();
                                          if (opensHour !== closesHour) {
                                            return;
                                          }
                                          const closesMinutes = moment(value).minutes();
                                          const opensMinutes = moment(form.getFieldValue(['businessHours', name, 'opensAt'])).minutes();
                                          if (opensMinutes > closesMinutes) {
                                            form.setFieldValue(['businessHours', name, 'closesAt'], moment(value).minutes(55));
                                          }
                                        }}
                                        disabledTime={() => ({
                                          disabledHours: () => {
                                            const hoursToDisable = [];
                                            for (const hour of HOURS) {
                                              const momentHour = moment(form.getFieldValue(['businessHours', name, 'opensAt'])).hour();
                                              const momentMinutes = moment(form.getFieldValue(['businessHours', name, 'opensAt'])).minutes();
                                              const shouldDisable = hour < momentHour || (hour === momentHour && momentMinutes < 5);
                                              if (shouldDisable) {
                                                hoursToDisable.push(hour);
                                              }
                                            }
                                            return hoursToDisable;
                                          },
                                          disabledMinutes: (hour) => {
                                            const minutesToDisable = [];
                                            for (const minute of MINUTES) {
                                              const momentMinute = moment().hour(hour).minutes(0).add(minute, 'minutes');
                                              const shouldDisable = moment(form.getFieldValue(['businessHours', name, 'opensAt'])).isSameOrAfter(momentMinute);
                                              if (shouldDisable) {
                                                minutesToDisable.push(minute);
                                              }
                                            }
                                            return minutesToDisable;
                                          }
                                        })}
                                        allowClear={false}
                                        placement="topLeft"
                                        showNow={false}
                                        format="HH:mm"
                                        minuteStep={5}
                                        className="fs-base"
                                        style={{ minHeight: 40 }}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col>
                                    <Form.Item
                                      label={<Typography.Text className="d-none">{t('Delete')}</Typography.Text>}
                                      labelCol={{ xs: 24 }}
                                    >
                                      <Button
                                        icon={<DeleteIcon />}
                                        shape="circle"
                                        type="text"
                                        className="text-muted mt-1"
                                        onClick={() => remove(name)}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Form.Item>
                            ))
                          }
                        </div>

                        <Button
                          type="dashed"
                          onClick={() => add({ day: 1, opensAt: moment().hour(9).minutes(0), closesAt: moment().hour(17).minutes(0) })}
                          className="text-muted"
                          icon={<PlusIcon />}
                          size="large"
                          disabled={!form.getFieldValue('group')}
                          block
                        >
                          {t('Add new slot')}
                        </Button>
                      </div>
                    )}
                  </Form.List>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
    </FormCard>
  );
};

export default LiveChatGeneralSettingsForm;
